import Joi from 'joi'
import { preMedDrugRouteIds } from './drug-routes'

const str = required => {
  const base = Joi.string().min(1).max(2000)
  return required ? base.required() : base
}

const mainNotesLimit = 5200

const macroSchema = Joi.object({
  copied: Joi.object({
    sedationLevel: Joi.string().valid(
      'Local',
      'Minimal',
      'Moderate',
      'Deep',
      'GA'
    ),
    ivEnabled: Joi.boolean(),
    airwayEnabled: Joi.boolean(),
    recorderName: str(),
    present: str(),
    userRole: Joi.string().valid('surgeon', 'sedationProvider', 'both'),
    asaClassification: str(),
    mallampati: str(),
    weightDistribution: Joi.string().valid(
      'Below waist',
      'Equal',
      'Waist and above'
    ),
    surgeonName: str(),
    sedationProviderName: str(),
    reasonForSedation: str(),
    pregnant: Joi.string().valid('n', 'y'),
    breastfeeding: Joi.boolean(),
    breastfeedingInstructionsGiven: Joi.boolean(),
    lastFood: str(),
    lastDrink: str(),
    procedureNotes: Joi.string().min(1).max(mainNotesLimit),
    recoveryNotes: Joi.string().min(1).max(mainNotesLimit),
    preProcedureRiskAssessment: str(),
    calculateDrugWaste: Joi.boolean(),
    dischargeScoreType: Joi.string().valid('pads', 'aldrete'),
    systemsReviewSkipped: Joi.boolean(),
    startedOnRoomAir: Joi.boolean(),
    preMedsUsed: Joi.boolean(),
    vitalsTrackingEnabled: Joi.boolean(),
  }),
  preMeds: Joi.array().items(
    Joi.object({
      drugId: str(true),
      drugName: str(true),
      dosage: str(),
      route: str().valid(...preMedDrugRouteIds),
      unit: str(),
    })
  ),
  iv: Joi.object({
    location: str(),
    needleGauge: str(),
    securedWith: str(),
    side: str(),
    solutionType: str(),
    attempts: Joi.number().min(1).max(50),
  }),
  pos: Joi.array().items(str()),
  airways: Joi.array().items(
    Joi.object({
      type: Joi.string().valid(
        'nc',
        'nt',
        'nh',
        'cpap',
        'mad',
        'et',
        'ntt',
        'lma',
        'opa',
        'fm',
        'other'
      ),
      name: str(),
      notes: str(),
      placementCompDetail: str(),
      vaso: str(),
      side: str(),
      size: str(),
      preOx: str(),
      magill: str(),
      scope: str(),
      peep: str(),
      maskType: str(),
      scopeType: str(),
      bladeSize: str(),
      viewGrade: str(),
      stylet: Joi.boolean(),
      cuffed: Joi.boolean(),
      inflated: Joi.boolean(),
      securedWith: str(),
      depth: str(),
      eyeProtection: str(),
      confirmation: str(),
      lube: Joi.boolean(),
      preWarmed: Joi.boolean(),
      // facemask
      sealQuality: str(),
      connectedTo: str(),
      maskHolder: str(),

      //  airway removal
      cavityClear: Joi.boolean(),
      removalComp: Joi.boolean(),
      removalCompDetail: str(),
      removalNotes: str(),

      // these are really clinical observations
      // that should be unique for all cases
      // so we no longer extract them when generating
      // a macro from a case, but we keep them here
      // to avoid validation issues if someone has
      // already saved a macro with these fields.
      preRemovalSpo2: str(),
      postRemovalSpo2: str(),

      deflated: Joi.boolean(),
    })
  ),
  healthyPatient: Joi.equal(true),
  vitalTypes: Joi.object(),
  localDrugs: Joi.array().items(
    Joi.object({
      id: str(true),
      name: str(true),
      rxcui: str(true),
      percent: Joi.number(),
      epi: Joi.number().min(0),
      dose: Joi.string(),
      unit: Joi.string(),
    })
  ),
  parenDrugs: Joi.array().items(
    Joi.object({
      id: str(true),
      name: str(true),
      route: str(),
      draw: Joi.string(),
      dose: Joi.string(),
      unit: Joi.string(),
    })
  ),
  infusionDrugs: Joi.array().items(
    Joi.object({
      id: str(true),
      name: str(true),
      dose: str(),
      unit: str(),
      bolusUnit: str(),
    })
  ),
  oralSedatives: Joi.array().items(
    Joi.object({
      id: str(true),
      name: str(true),
      unit: str(),
      dose: str(),
      route: str(),
      preProcedure: Joi.boolean(),
    })
  ),
})

export const validateMacro = obj => Joi.assert(obj, macroSchema)
